import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../useAuth";
import { canAccessAdminModules } from "../../accessControl";
import {
  listTaxonomyValues,
  createTaxonomyValue,
  updateTaxonomyValue,
} from "../../api/taxonomy.api";
import {
  DEFAULT_CONTROL_REVIEW_TEMPLATE,
  getControlReviewTemplate,
  updateControlReviewTemplate,
} from "../../api/controlReviewTemplate.api";
import {
  getControlEffectivenessConfig,
  updateControlEffectivenessConfig,
} from "../../api/controlEffectivenessConfig.api";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import RichTextEditor from "../../ui/RichTextEditor";
import ControlEffectivenessBadge from "../../components/controls/ControlEffectivenessBadge";
import { CONTROL_EFFECTIVENESS_CONFIG_EVENT } from "../../hooks/useControlEffectivenessConfig";
import { spacing } from "../../ui/tokens";
import {
  CONTROL_REVIEW_TEMPLATE_PLACEHOLDERS,
  extractTemplateTokenNames,
  ensureRichTextHtml,
  isRichTextEmpty,
  sanitizeRichTextHtml,
} from "../../utils/riskReviewTemplate";
import {
  mergeControlEffectivenessConfig,
  isValidControlEffectivenessColor,
} from "../../utils/controlEffectiveness";

const CATEGORY_KEY = "control_category";
const CONTROL_EFFECTIVENESS_LABELS = {
  effective: "Effective",
  needs_improvement: "Partially Effective",
  ineffective: "Ineffective",
  unrated: "Not Assessed",
};
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
const CONTROL_TEMPLATE_PLACEHOLDER_OPTIONS = CONTROL_REVIEW_TEMPLATE_PLACEHOLDERS.map((placeholder) => ({
  name: placeholder.name,
  label: placeholder.label,
}));

const toSlug = (value = "") =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export default function ControlCategoriesAdmin() {
  const { user } = useAuth();
  const canManageAdminModules = canAccessAdminModules(user);
  const isAdmin = canManageAdminModules;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState({ label: "", value: "", status: "active" });
  const [editingId, setEditingId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [reviewTemplate, setReviewTemplate] = useState(() => ({
    template_body: ensureRichTextHtml(DEFAULT_CONTROL_REVIEW_TEMPLATE.template_body),
  }));
  const [reviewTemplateLoading, setReviewTemplateLoading] = useState(true);
  const [reviewTemplateSaving, setReviewTemplateSaving] = useState(false);
  const [reviewTemplateMessage, setReviewTemplateMessage] = useState(null);
  const [effectivenessItems, setEffectivenessItems] = useState(() => mergeControlEffectivenessConfig());
  const [effectivenessLoading, setEffectivenessLoading] = useState(true);
  const [effectivenessSaving, setEffectivenessSaving] = useState(false);
  const [effectivenessMessage, setEffectivenessMessage] = useState(null);
  const [effectivenessErrors, setEffectivenessErrors] = useState({});

  const activeCount = useMemo(
    () => categories.filter((c) => String(c.status || "active").toLowerCase() === "active").length,
    [categories]
  );
  const usedReviewTemplateTokens = useMemo(
    () =>
      extractTemplateTokenNames(
        reviewTemplate?.template_body || "",
        CONTROL_REVIEW_TEMPLATE_PLACEHOLDERS.map((token) => token.name)
      ),
    [reviewTemplate?.template_body]
  );

  const loadCategories = useCallback(() => {
    setLoading(true);
    setError(null);
    listTaxonomyValues(CATEGORY_KEY)
      .then((res) => setCategories(res?.data || []))
      .catch((err) => {
        setCategories([]);
        setError(err?.response?.data?.detail || err?.message || "Unable to load control settings.");
      })
      .finally(() => setLoading(false));
  }, []);

  const loadReviewTemplate = useCallback(() => {
    setReviewTemplateLoading(true);
    setReviewTemplateMessage(null);
    getControlReviewTemplate({ force: true })
      .then((res) => {
        const payload = res?.data || {};
        setReviewTemplate({
          template_body: ensureRichTextHtml(payload?.template_body || DEFAULT_CONTROL_REVIEW_TEMPLATE.template_body),
        });
      })
      .catch((_err) => {
        setReviewTemplate({
          template_body: ensureRichTextHtml(DEFAULT_CONTROL_REVIEW_TEMPLATE.template_body),
        });
      })
      .finally(() => setReviewTemplateLoading(false));
  }, []);

  const loadEffectivenessConfig = useCallback(() => {
    setEffectivenessLoading(true);
    setEffectivenessMessage(null);
    getControlEffectivenessConfig({ force: true })
      .then((res) => {
        setEffectivenessItems(mergeControlEffectivenessConfig(res?.data?.items || []));
      })
      .catch(() => {
        setEffectivenessItems(mergeControlEffectivenessConfig());
      })
      .finally(() => setEffectivenessLoading(false));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadCategories();
    loadReviewTemplate();
    loadEffectivenessConfig();
  }, [isAdmin, loadCategories, loadReviewTemplate, loadEffectivenessConfig]);

  const resetForm = () => {
    setFormState({ label: "", value: "", status: "active" });
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formState.label?.trim()) {
      setActionMessage({ tone: "error", text: "Category label is required." });
      return;
    }
    const value = formState.value?.trim() || toSlug(formState.label);
    if (!value) {
      setActionMessage({ tone: "error", text: "Category value is required." });
      return;
    }
    setSaving(true);
    setActionMessage(null);
    try {
      if (editingId) {
        await updateTaxonomyValue(CATEGORY_KEY, editingId, {
          label: formState.label.trim(),
          value,
          status: formState.status,
        });
        setActionMessage({ tone: "success", text: "Category updated." });
      } else {
        await createTaxonomyValue(CATEGORY_KEY, {
          label: formState.label.trim(),
          value,
          status: formState.status,
        });
        setActionMessage({ tone: "success", text: "Category created." });
      }
      resetForm();
      loadCategories();
    } catch (err) {
      setActionMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Unable to save category.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormState({
      label: category.label || "",
      value: category.value || "",
      status: category.status || "active",
    });
    setActionMessage(null);
  };

  const toggleStatus = async (category) => {
    if (!category?.id) return;
    const nextStatus = String(category.status || "active").toLowerCase() === "active" ? "inactive" : "active";
    setSaving(true);
    setActionMessage(null);
    try {
      await updateTaxonomyValue(CATEGORY_KEY, category.id, { status: nextStatus });
      setActionMessage({
        tone: "success",
        text: nextStatus === "active" ? "Category activated." : "Category deactivated.",
      });
      loadCategories();
    } catch (err) {
      setActionMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Unable to update status.",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveReviewTemplate = async () => {
    if (reviewTemplateSaving) return;
    const payload = {
      template_body: sanitizeRichTextHtml(reviewTemplate?.template_body || ""),
    };
    if (isRichTextEmpty(payload.template_body)) {
      setReviewTemplateMessage({ tone: "error", text: "Review note template is required." });
      return;
    }

    setReviewTemplateSaving(true);
    setReviewTemplateMessage(null);
    try {
      const res = await updateControlReviewTemplate(payload);
      const data = res?.data || payload;
      setReviewTemplate({
        template_body: ensureRichTextHtml(data?.template_body || DEFAULT_CONTROL_REVIEW_TEMPLATE.template_body),
      });
      setReviewTemplateMessage({ tone: "success", text: "Control review template saved." });
      window.dispatchEvent(new CustomEvent("controlReviewTemplateChanged"));
    } catch (err) {
      setReviewTemplateMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Failed to save control review template.",
      });
    } finally {
      setReviewTemplateSaving(false);
    }
  };

  const resetReviewTemplate = () => {
    setReviewTemplate({
      template_body: ensureRichTextHtml(DEFAULT_CONTROL_REVIEW_TEMPLATE.template_body),
    });
    setReviewTemplateMessage(null);
  };

  const handleEffectivenessFieldChange = (key, field, value) => {
    setEffectivenessItems((current) =>
      current.map((item) =>
        item.value === key
          ? {
              ...item,
              [field]: field === "color" ? String(value || "").toUpperCase() : value,
            }
          : item
      )
    );
    setEffectivenessErrors((current) => {
      if (!current[`${key}.${field}`]) return current;
      const next = { ...current };
      delete next[`${key}.${field}`];
      return next;
    });
    setEffectivenessMessage(null);
  };

  const saveEffectivenessConfig = async () => {
    if (effectivenessSaving) return;
    const nextErrors = {};

    effectivenessItems.forEach((item) => {
      if (!String(item.label || "").trim()) {
        nextErrors[`${item.value}.label`] = "Display label is required.";
      }
      if (!isValidControlEffectivenessColor(item.color)) {
        nextErrors[`${item.value}.color`] = "Use a valid #RRGGBB colour.";
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setEffectivenessErrors(nextErrors);
      setEffectivenessMessage({ tone: "error", text: "Fix the effectiveness labels and colours before saving." });
      return;
    }

    setEffectivenessSaving(true);
    setEffectivenessErrors({});
    setEffectivenessMessage(null);
    try {
      const response = await updateControlEffectivenessConfig({
        items: mergeControlEffectivenessConfig(effectivenessItems),
      });
      setEffectivenessItems(mergeControlEffectivenessConfig(response?.data?.items || []));
      setEffectivenessMessage({ tone: "success", text: "Control effectiveness ratings saved." });
      window.dispatchEvent(new CustomEvent(CONTROL_EFFECTIVENESS_CONFIG_EVENT));
    } catch (err) {
      setEffectivenessMessage({
        tone: "error",
        text: err?.response?.data?.detail || err?.message || "Failed to save control effectiveness ratings.",
      });
    } finally {
      setEffectivenessSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div style={guardStyle}>
        <h2 style={{ marginTop: 0 }}>Control Settings</h2>
        <p style={{ marginBottom: 0 }}>
          You need Manage access to Admin Module Portal to manage control settings.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: spacing.xl }}>
      <div>
        <h2 style={{ marginBottom: spacing.xs }}>Control Settings</h2>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
          Manage control categories and the review note template used during control reviews.
        </p>
      </div>

      <div style={summaryCardStyle}>
        <div style={{ fontWeight: 600 }}>Active categories</div>
        <div style={{ fontSize: 28 }}>{activeCount}</div>
      </div>

      <div style={formCardStyle}>
        <h3 style={{ marginTop: 0 }}>Control effectiveness ratings</h3>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
          Customize the user-facing wording and colours for control effectiveness badges. Internal keys stay fixed.
        </p>
        <div style={{ marginTop: spacing.md, display: "grid", gap: spacing.md }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Internal key</th>
                <th style={thStyle}>Display label</th>
                <th style={thStyle}>Colour</th>
                <th style={thStyle}>Preview</th>
              </tr>
            </thead>
            <tbody>
              {effectivenessItems.map((item) => (
                <tr key={item.value}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{CONTROL_EFFECTIVENESS_LABELS[item.value] || item.value}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{item.value}</div>
                  </td>
                  <td style={tdStyle}>
                    <Input
                      value={item.label}
                      onChange={(e) => handleEffectivenessFieldChange(item.value, "label", e.target.value)}
                      error={effectivenessErrors[`${item.value}.label`]}
                      disabled={effectivenessLoading || effectivenessSaving}
                    />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "grid", gap: spacing.xs }}>
                      <input
                        type="color"
                        value={item.color}
                        onChange={(e) => handleEffectivenessFieldChange(item.value, "color", e.target.value)}
                        disabled={effectivenessLoading || effectivenessSaving}
                        style={colorInputStyle}
                        aria-label={`${item.value} colour picker`}
                      />
                      <Input
                        value={item.color}
                        onChange={(e) => handleEffectivenessFieldChange(item.value, "color", e.target.value)}
                        error={effectivenessErrors[`${item.value}.color`]}
                        disabled={effectivenessLoading || effectivenessSaving}
                        ariaLabel={`${item.value} colour hex`}
                      />
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <ControlEffectivenessBadge value={item.value} config={effectivenessItems} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {effectivenessMessage && (
            <div style={effectivenessMessage.tone === "error" ? errorBanner : successBanner}>
              {effectivenessMessage.text}
            </div>
          )}
          <div style={{ display: "flex", gap: spacing.sm }}>
            <Button
              type="button"
              variant="secondary"
              onClick={loadEffectivenessConfig}
              disabled={effectivenessSaving || effectivenessLoading}
            >
              Reset
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={saveEffectivenessConfig}
              disabled={effectivenessSaving || effectivenessLoading}
            >
              {effectivenessSaving ? "Saving..." : "Save Ratings"}
            </Button>
          </div>
        </div>
      </div>

      <div style={formCardStyle}>
        <h3 style={{ marginTop: 0 }}>Control Review Template</h3>
        <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
          Configure the template used to pre-populate control review notes before the reviewer edits and saves them.
        </p>
        <div style={{ marginTop: spacing.md, display: "grid", gap: spacing.sm }}>
          <div style={placeholderReferenceStyle}>
            <div style={placeholderReferenceTitleStyle}>Placeholder Reference</div>
            <div style={placeholderReferenceGridStyle}>
              {CONTROL_REVIEW_TEMPLATE_PLACEHOLDERS.map((token) => (
                <div
                  key={token.name}
                  style={{
                    ...placeholderCardStyle,
                    ...(usedReviewTemplateTokens.has(token.name) ? placeholderCardActiveStyle : null),
                  }}
                >
                  <div style={placeholderCardHeaderStyle}>
                    <div style={placeholderLabelStyle}>{token.label}</div>
                    {usedReviewTemplateTokens.has(token.name) && (
                      <span style={placeholderBadgeStyle}>In use</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gap: spacing.xs }}>
            <span style={labelText}>Review Note Template</span>
            <RichTextEditor
              value={reviewTemplate.template_body}
              onChange={(nextValue) =>
                setReviewTemplate((prev) => ({ ...prev, template_body: nextValue || "<p></p>" }))
              }
              disabled={reviewTemplateLoading || reviewTemplateSaving}
              placeholderTokens={CONTROL_TEMPLATE_PLACEHOLDER_OPTIONS}
              minHeight={160}
            />
          </div>
          {reviewTemplateMessage && (
            <div style={reviewTemplateMessage.tone === "error" ? errorBanner : successBanner}>
              {reviewTemplateMessage.text}
            </div>
          )}
          <div style={{ display: "flex", gap: spacing.sm }}>
            <Button
              type="button"
              variant="secondary"
              onClick={resetReviewTemplate}
              disabled={reviewTemplateSaving || reviewTemplateLoading}
            >
              Reset to Default
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={saveReviewTemplate}
              disabled={reviewTemplateSaving || reviewTemplateLoading}
            >
              {reviewTemplateSaving ? "Saving..." : "Save Template"}
            </Button>
          </div>
        </div>
      </div>

      <div style={formCardStyle}>
        <h3 style={{ marginTop: 0 }}>{editingId ? "Edit category" : "Add category"}</h3>
        <form onSubmit={handleSubmit} style={{ display: "grid", gap: spacing.md }}>
          <Input
            label="Label"
            value={formState.label}
            onChange={(e) => setFormState((prev) => ({ ...prev, label: e.target.value }))}
            placeholder="e.g., Information Security"
          />
          <Input
            label="Value"
            value={formState.value}
            onChange={(e) => setFormState((prev) => ({ ...prev, value: e.target.value }))}
            placeholder="Optional machine key (auto-generated if blank)"
          />
          <label style={fieldLabel}>
            <span style={labelText}>Status</span>
            <Select
              value={formState.status}
              onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value }))}
              style={inputStyle}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </label>

          {actionMessage && (
            <div style={actionMessage.tone === "error" ? errorBanner : successBanner}>
              {actionMessage.text}
            </div>
          )}

          <div style={{ display: "flex", gap: spacing.sm }}>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save changes" : "Create category"}
            </Button>
            {editingId && (
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>

      <div style={tableCardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>All categories</h3>
          {loading && <span style={{ color: "#6b7280" }}>Loading...</span>}
        </div>

        {error && <div style={errorBanner}>{error}</div>}

        {!loading && !categories.length && (
          <div style={{ padding: spacing.md, color: "#6b7280" }}>
            No categories configured yet. Add your first category above.
          </div>
        )}

        {categories.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Label</th>
                <th style={thStyle}>Value</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const statusValue = String(category.status || "active").toLowerCase();
                return (
                  <tr key={category.id}>
                    <td style={tdStyle}>{category.label}</td>
                    <td style={tdStyle}>{category.value}</td>
                    <td style={tdStyle}>
                      <span style={statusValue === "inactive" ? inactiveTag : activeTag}>
                        {statusValue}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: spacing.sm }}>
                        <Button variant="secondary" onClick={() => handleEdit(category)}>
                          Edit
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => toggleStatus(category)}
                          disabled={saving}
                        >
                          {statusValue === "inactive" ? "Activate" : "Deactivate"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const formCardStyle = {
  background: "white",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
  boxShadow: "0 8px 16px rgba(15,23,42,0.04)",
};

const tableCardStyle = {
  background: "white",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
};

const summaryCardStyle = {
  background: "#f8fafc",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: spacing.lg,
  display: "grid",
  gap: spacing.xs,
  maxWidth: 240,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: spacing.md,
};

const thStyle = {
  textAlign: "left",
  fontSize: 12,
  color: "#6b7280",
  paddingBottom: spacing.sm,
  borderBottom: "1px solid #e5e7eb",
};

const tdStyle = {
  padding: `${spacing.sm}px 0`,
  borderBottom: "1px solid #f1f5f9",
  fontSize: 13,
};

const activeTag = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 12,
  fontWeight: 600,
};

const inactiveTag = {
  display: "inline-flex",
  padding: "2px 10px",
  borderRadius: 999,
  background: "#e2e8f0",
  color: "#475569",
  fontSize: 12,
  fontWeight: 600,
};

const guardStyle = {
  padding: spacing.lg,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
};

const fieldLabel = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
};

const labelText = {
  fontSize: 12,
  color: "#6b7280",
};

const placeholderReferenceStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  background: "#ffffff",
  padding: spacing.sm,
  display: "grid",
  gap: spacing.xs,
};

const placeholderReferenceTitleStyle = {
  fontSize: 12,
  color: "#475569",
  fontWeight: 700,
};

const placeholderReferenceGridStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
};

const placeholderCardStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  background: "#f8fafc",
  padding: `${spacing.xs}px ${spacing.sm}px`,
  display: "grid",
  gap: spacing.xs / 2,
  minWidth: 160,
  flex: "1 1 180px",
};

const placeholderCardActiveStyle = {
  border: "1px solid #16a34a",
  background: "#f0fdf4",
  boxShadow: "inset 0 0 0 1px rgba(22,163,74,0.12)",
};

const placeholderCardHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.xs,
};

const placeholderLabelStyle = {
  fontSize: 12,
  color: "#0f172a",
  fontWeight: 600,
};

const placeholderBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  background: "#16a34a",
  color: "#ffffff",
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: "nowrap",
};


const inputStyle = {
  width: "100%",
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: 6,
  border: "1px solid #d1d5db",
};

const colorInputStyle = {
  width: 48,
  height: 32,
  padding: 0,
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "#ffffff",
};

const errorBanner = {
  padding: spacing.sm,
  borderRadius: 8,
  background: "#fee2e2",
  color: "#b91c1c",
  fontSize: 13,
};

const successBanner = {
  padding: spacing.sm,
  borderRadius: 8,
  background: "#dcfce7",
  color: "#166534",
  fontSize: 13,
};
